import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { z } from "zod";
import {
  GroupSelect,
  SelectField,
  userRole,
  userRoleOptions,
  uuid,
} from "../../../components/Form";
import { useStudioForm } from "../../../components/Form/hooks";
import { useCreateGroupAssociation } from "../../../domain/crud";
import type { GroupAssociation, User } from "../../../services/datastore";
import { UserRole } from "../../../services/datastore";

export interface AddToGroupDialogProps {
  userId: User["id"];
  open: boolean;
  onClose: () => void;
  groupAssociations: readonly GroupAssociation[];
}

const DEFAULT_VALUES = {
  groupId: null,
  role: UserRole.Viewer,
};

export function AddToGroupDialog({
  userId,
  open,
  onClose,
  groupAssociations,
}: AddToGroupDialogProps) {
  const schema = makeSchema(groupAssociations);

  const { enqueueSnackbar } = useSnackbar();

  const createGroupAssociation = useCreateGroupAssociation();

  const { control, handleSubmit, reset } = useStudioForm({
    schema,
    defaultValues: DEFAULT_VALUES,
    onSubmit({ groupId, role }) {
      createGroupAssociation.mutate(
        { userId, groupId, role },
        {
          onSuccess() {
            onClose();

            enqueueSnackbar("User added to group", { variant: "success" });
          },
          onError() {
            enqueueSnackbar("Unable to add user to group", {
              variant: "error",
            });
          },
        }
      );
    },
  });

  function handleClose() {
    if (createGroupAssociation.isLoading) {
      return;
    }

    onClose();
  }

  function handleExited() {
    createGroupAssociation.reset();
    reset(DEFAULT_VALUES);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{
        onExited: handleExited,
      }}
    >
      <DialogTitle>Add User to a Group</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <Stack component="form" onSubmit={handleSubmit} spacing={2}>
          <GroupSelect control={control} name="groupId" label="Group" />
          <SelectField
            control={control}
            name="role"
            label="Role"
            options={userRoleOptions}
          />
          <Stack direction="row" spacing={2} justifyContent="end">
            <Button
              disabled={createGroupAssociation.isLoading}
              onClick={handleClose}
            >
              Close
            </Button>
            <LoadingButton
              loading={createGroupAssociation.isLoading}
              type="submit"
              variant="contained"
            >
              Submit
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function makeSchema(groupAssociations: readonly GroupAssociation[]) {
  return z.object({
    groupId: uuid.refine(
      (value) =>
        !groupAssociations.some(
          (groupAssociation) => groupAssociation.groupId === value
        ),
      "User is already a member of this group"
    ),
    role: userRole,
  });
}
